<template>
  <div class="page-container">
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <Sticky>
        <van-tabs
          v-model:active="couponStatus"
          color="#ff6262"
          line-width="28px"
          :disabled="loading"
          :title-inactive-color="blackColor6"
          :title-active-color="blackColor2"
          @click-tab="onClickTab"
          class="tabs"
        >
          <van-tab v-for="item in CouponListTabs" :name="item.name" :key="item.name" :title="item.title" />
        </van-tabs>
      </Sticky>
      <div class="coupon-list-box">
        <van-list
          class="list-container"
          v-model:loading="loading"
          :finished="finished"
          :finished-text="finishedText"
          :immediate-check="false"
          @load="getList"
        >
          <div
            class="coupon-list-item-wrp"
            :class="{ cover: [CouponStatusEnum.HAS_USE, CouponStatusEnum.HAS_NOT_EFFECT].includes(item.status) }"
            v-for="item in list"
            :key="item.coupon_id"
          >
            <van-image
              v-if="[CouponStatusEnum.HAS_USE, CouponStatusEnum.HAS_NOT_EFFECT].includes(item.status)"
              class="effect-stamp"
              :src="CouponStatusEnum.HAS_USE === item.status ? couponStampUse : couponStampEffect"
              :width="transPxToRem(60)"
            />
            <div class="coupon-list-item" @click="goSelectCouponGoods({ couponStatus: item.status, rid: item.rid })">
              <div class="coupon-content">
                <div class="coupon-item-left">
                  <div class="coupon-type">{{ item.use_type }}</div>
                  <couponPrice
                    :price="item.coupon_value"
                    :color="
                      [CouponStatusEnum.HAS_USE, CouponStatusEnum.HAS_NOT_EFFECT].includes(item.status)
                        ? '#fff'
                        : '#fe2c38'
                    "
                  />
                  <div class="full-amount-des">
                    {{
                      item.use_condition_type === 1
                        ? '无门槛'
                        : `满${item.use_condition_value}${item.use_condition_type === 2 ? '元' : '件'}可用`
                    }}
                  </div>
                </div>
                <div class="divider-box"></div>
                <div class="coupon-item-center">
                  <div class="coupon-name van-ellipsis">{{ item.coupon_name }}</div>
                  <div class="use-des">{{ item.use_type }}</div>
                  <div class="coupon-effect-text">
                    <template v-if="item.status === CouponStatusEnum.WAIT_USE">有效期至：{{ item.end_time }}</template>
                    <template v-if="item.status === CouponStatusEnum.NOT_EFFECT"
                      >生效期起：{{ item.start_time }}</template
                    >
                    <template v-if="item.status === CouponStatusEnum.HAS_USE">使用时间：{{ item.use_time }}</template>
                    <template v-if="item.status === CouponStatusEnum.HAS_NOT_EFFECT">
                      {{ item.start_time }}-{{ item.end_time }}
                    </template>
                  </div>
                </div>
                <div class="coupon-item-right flex">
                  <van-button
                    v-if="[CouponStatusEnum.NOT_EFFECT, CouponStatusEnum.WAIT_USE].includes(item.status)"
                    class="btn go-use-btn"
                    :class="{ 'go-use-btn-disabled': item.status === CouponStatusEnum.NOT_EFFECT }"
                    >去{{ item.status === CouponStatusEnum.NOT_EFFECT ? '看看' : '使用' }}</van-button
                  >

                  <div class="coupon-status-tag" v-if="item.status === CouponStatusEnum.NOT_EFFECT">未生效</div>
                </div>
              </div>
              <ul class="des-up-block" v-if="item.isShowDes && item.desList.length > 0">
                <template v-for="(desItem, index) in item.desList" :key="index">
                  <li
                    v-if="desItem"
                    class="list-item"
                    :class="{ 'has-list-dot': item.desList.filter(d => d).length > 1 }"
                  >
                    {{ desItem }}
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </van-list>
        <van-empty
          v-if="list.length == 0 && !loading"
          image-size="240"
          image="https://i.xurl.cn/x_dsx_wxapp/empty/coupon.png"
          description="暂无优惠券"
        />
      </div>
    </van-pull-refresh>
    <div class="footer-bar btnfooter" @click="goCouponCenter">去领券中心 <van-icon name="arrow" /></div>
  </div>
</template>
<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { getMemberCouponList } from '@/api/coupon'
import couponPrice from './component/couponPrice.vue'
import { CouponListTabs, CouponStatusEnum } from './coupon.data'
import { useCssVar } from '@vueuse/core'
import usePage from '@/hooks/usePage'
import couponStampEffect from '@/assets/img/coupon_stamp-effect.png'
import couponStampUse from '@/assets/img/coupon_stamp-use.png'
import { transPxToRem } from '@/utils'
import { Sticky } from 'vant'

const { navigateTo } = usePage()
const couponStatus = ref(CouponStatusEnum.WAIT_USE)
const pageParams = reactive({
  page: 1,
  pageSize: 20
})
const loading = ref(false)
const finished = ref(false)
const list = ref([])
const finishedText = computed(() => (list.value.length ? '到达地平线' : ''))

const reset = () => {
  finished.value = false
  list.value = []
  pageParams.page = 1
}

const getList = async () => {
  loading.value = true
  try {
    const resData = await getMemberCouponList({ ...pageParams, coupon_status: couponStatus.value, coupon_type: 1 })
    list.value.push(
      ...resData.list.map(d => ({
        ...d,
        isShowDes: true,
        desList: d.remark ? [`备注：${d.remark}`] : []
      }))
    )
    pageParams.page++
    finished.value = list.value.length >= +resData.total
  } catch {
    finished.value = true
  } finally {
    loading.value = false
  }
}

const onClickTab = () => {
  reset()
  getList()
}

const goCouponCenter = () => {
  navigateTo('/coupon')
}

const goSelectCouponGoods = ({ couponStatus, rid }) => {
  if ([CouponStatusEnum.WAIT_USE, CouponStatusEnum.NOT_EFFECT].includes(couponStatus)) {
    navigateTo(`/couponGoodsList?rid=${rid}`)
  }
}

const refreshing = ref(false)
const onRefresh = async () => {
  refreshing.value = true
  try {
    onClickTab()
  } finally {
    refreshing.value = false
  }
}

// const primaryColor = useCssVar('--van-primary-color', document.documentElement)
const blackColor6 = useCssVar('--van-black-6', document.documentElement)
const blackColor2 = useCssVar('--van-black-2', document.documentElement)

onMounted(async () => {
  await getList()
  // document.getElementById('app').style.background = '#fd6f54'
})
</script>

<style lang="less" scoped>
@primary-color: #fd6f54;
@font-primary-color: #ff6262;
.page-container {
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: calc(5px + env(safe-area-inset-bottom));
  overflow: auto;
  .tabs {
    :deep(.van-tabs__line) {
      border-radius: 2px;
    }
  }
  .coupon-list-box {
    padding-bottom: 60px;
    .coupon-list-item-wrp {
      margin: 12px;
      .coupon-list-item {
        background-color: white;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0px 2px 6px 0px rgba(34, 34, 34, 0.04);
        .coupon-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
        }
        .coupon-item-left {
          text-align: center;
          width: 95px;
          .coupon-type {
            width: 90px;
            height: 18px;
            line-height: 18px;
            color: #8c6243;
            font-size: 10px;
            box-sizing: border-box;
            padding-left: 8px;
            position: absolute;
            top: 0px;
            text-align: left;
            background: linear-gradient(90deg, #fbebc5 0%, rgba(252, 231, 190, 0) 100%);
            border-radius: 8px 0px 8px 0px;
          }
          // .coupon-amount-box {
          //   font-size: 16px;
          //   font-weight: 600;
          //   color: @font-primary-color;
          //   font-family: Avenir;
          //   .coupon-amount {
          //     font-size: 32px;
          //   }
          // }
          .full-amount-des {
            //   margin-top: 4px;
            font-weight: 500;
            color: var(--van-black-9);
            font-size: 11px;
            font-family: Avenir;
          }
        }
        .divider-box {
          position: relative;
          border-left: dashed #d8d8d8 1px;
          height: 88px;
          padding: 0 8px;
          .red-circle {
            content: '';
            display: block;
            position: absolute;
            width: 10px;
            height: 10px;
            background-color: #f8f8f8;
            border-radius: 50%;
            left: -6px;
          }
          &::before {
            .red-circle();
            top: -13px;
          }
          &::after {
            .red-circle();
            bottom: -13px;
          }
        }
        .coupon-item-center {
          flex: 1;
          .coupon-name {
            font-size: 16px;
            font-weight: 600;
            color: var(--van-black-2);
            max-width: 165px;
          }
          .coupon-effect-text {
            position: relative;
            font-size: 12px;
            color: var(--van-black-6);
            font-weight: 400;
          }
          .use-des {
            margin: 8px 0;
            color: var(--van-black-9);
            font-weight: 400;
            font-size: 12px;
            display: inline-block;
            .arrow-down-wrp {
              background-color: #f5f8f8;
              border-radius: 50%;
              display: inline-block;
              padding: 2px;
              line-height: 0;
            }
          }
        }
        .coupon-item-right {
          flex-direction: column;
          padding-right: 10px;
          .btn {
            height: 24px;
            font-size: 12px;
            border: none;
            width: 60px;
            padding: 0;
            border-radius: 14px;
          }
          .go-use-btn {
            background: linear-gradient(180deg, #fe5f70 0%, #fe3a4a 100%);
            border-radius: 14px;
            color: #fff;
            margin-top: 5px;
          }
          .btn-primary {
            border-radius: 14px;
            border: 1px solid #d8d8d8;
            color: #222222;
            margin-top: 5px;
          }
          .coupon-status-tag {
            position: absolute;
            background-color: @primary-color;
            color: #fff;
            font-size: 10px;
            transform: rotate(45deg);
            width: 60px;
            top: 6px;
            right: -13px;
            text-align: center;
          }
          .go-use-btn-disabled {
            opacity: 0.7;
          }
        }
      }
      .des-up-block {
        background: #ffffff;
        color: #999;
        font-size: 12px;
        padding: 8px 12px;
        border-radius: 0px 0px 8px 8px;
        .list-item {
          height: 18px;
          line-height: 18px;
          position: relative;
        }
        .has-list-dot {
          &::before {
            position: absolute;
            content: '.';
            display: inline-block;
            left: -5px;
            top: -4px;
          }
        }
      }
    }
    .cover {
      position: relative;
      .effect-stamp {
        position: absolute;
        right: 19px;
        top: 22px;
        z-index: 1;
      }
      .coupon-list-item {
        opacity: 0.6;
        .coupon-item-left {
          background-color: #d8d8d8;
          padding: 21.55px 0;
          // width: 97px;
          .coupon-type {
            display: none;
          }
          .coupon-amount-box {
            color: #fff;
          }
          .full-amount-des {
            color: #fff;
          }
        }
        .divider-box {
          border-left: none;
          &::before {
            content: none;
          }
          &::after {
            content: none;
          }
        }
      }
    }
  }
}
.footer-bar {
  text-align: center;
  height: 44px;
  line-height: 44px;
  background: #ffffff;
  box-shadow: 0px -4px 10px 0px rgba(15, 15, 16, 0.08);
  font-size: 14px;
  bottom: 0;
  box-sizing: content-box;
  color: #222;
  font-weight: 500;
  z-index: 2;
}
</style>
